import { AlertTriangle, CheckCircle2, Info, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export type AppModalTone = "info" | "success" | "warning" | "error";

export interface AppConfirmModalOption {
  id: string;
  label: string;
  variant?: "default" | "outline" | "secondary" | "destructive";
}

interface AppConfirmModalProps {
  open: boolean;
  title: string;
  description?: string;
  tone?: AppModalTone;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
  options?: AppConfirmModalOption[];
  onChoose?: (id: string) => void;
  promptLabel?: string;
  promptPlaceholder?: string;
  promptValue?: string;
  onPromptValueChange?: (value: string) => void;
  promptError?: string | null;
}

const toneStyles: Record<AppModalTone, string> = {
  info: "text-sky-600",
  success: "text-emerald-600",
  warning: "text-amber-600",
  error: "text-destructive",
};

function ToneIcon({ tone = "info" }: { tone?: AppModalTone }) {
  if (tone === "success")
    return <CheckCircle2 className={`h-5 w-5 ${toneStyles[tone]}`} />;
  if (tone === "warning")
    return <AlertTriangle className={`h-5 w-5 ${toneStyles[tone]}`} />;
  if (tone === "error")
    return <XCircle className={`h-5 w-5 ${toneStyles[tone]}`} />;
  return <Info className={`h-5 w-5 ${toneStyles[tone]}`} />;
}

export function AppConfirmModal({
  open,
  title,
  description,
  tone = "info",
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  onConfirm,
  onCancel,
  loading = false,
  options,
  onChoose,
  promptLabel,
  promptPlaceholder,
  promptValue,
  onPromptValueChange,
  promptError,
}: AppConfirmModalProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <ToneIcon tone={tone} />
            <span>{title}</span>
          </DialogTitle>
          <DialogDescription className={description ? undefined : "sr-only"}>
            {description ?? "Diálogo de la aplicación."}
          </DialogDescription>
        </DialogHeader>

        {promptLabel ? (
          <div className="space-y-2">
            <Label>{promptLabel}</Label>
            <Input
              value={promptValue ?? ""}
              placeholder={promptPlaceholder}
              onChange={(event) => onPromptValueChange?.(event.target.value)}
            />
            {promptError ? (
              <p className="text-xs text-destructive">{promptError}</p>
            ) : null}
          </div>
        ) : null}

        {options?.length && onChoose ? (
          <DialogFooter className="sm:justify-start">
            <div className="flex w-full flex-wrap justify-end gap-2">
              {options.map((option) => (
                <Button
                  key={option.id}
                  type="button"
                  variant={option.variant ?? "outline"}
                  onClick={() => onChoose(option.id)}
                  disabled={loading}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </DialogFooter>
        ) : (
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              {cancelText}
            </Button>
            <Button
              type="button"
              variant={tone === "error" ? "destructive" : "default"}
              onClick={onConfirm}
              loading={loading}
            >
              {confirmText}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
